import React, { useState, useEffect, useRef } from 'react';
import { Search, X, ChevronDown, Check, Loader2, Star, Plus, AlertTriangle } from 'lucide-react';
import { TradingPair } from '../types/api';
import { tradingPairsAPI } from '../services/api';
import { cn } from '../lib/utils';
import { SymbolValidator, SymbolValidationResult } from '../lib/symbolValidator';

interface TradingPairSelectorProps {
  selectedPairs: string[];
  onSelectionChange: (pairs: string[]) => void;
  testnet?: boolean;
  className?: string;
}

const TradingPairSelector: React.FC<TradingPairSelectorProps> = ({
  selectedPairs,
  onSelectionChange,
  testnet = true,
  className
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [allPairs, setAllPairs] = useState<TradingPair[]>([]);
  const [popularPairs, setPopularPairs] = useState<TradingPair[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [customPairInput, setCustomPairInput] = useState('');
  const [customPairValidation, setCustomPairValidation] = useState<SymbolValidationResult>({ isValid: true });
  const [showAddCustom, setShowAddCustom] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Popular pairs list for quick selection
  const POPULAR_PAIRS = [
    'BTC/USDT', 'ETH/USDT', 'BNB/USDT', 'XRP/USDT', 'ADA/USDT', 
    'SOL/USDT', 'DOT/USDT', 'AVAX/USDT', 'MATIC/USDT', 'LINK/USDT'
  ];

  useEffect(() => {
    if (isOpen && allPairs.length === 0) {
      fetchTradingPairs();
    }
  }, [isOpen, testnet]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // Close dropdown when clicking outside
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    // Focus search input when dropdown opens
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  const fetchTradingPairs = async () => {
    setLoading(true);
    setError('');
    try {
      const [pairs, popular] = await Promise.all([
        tradingPairsAPI.getTradingPairs(testnet),
        tradingPairsAPI.getPopularTradingPairs(testnet).catch(() => [])
      ]);
      
      setAllPairs(pairs);
      setPopularPairs(popular.length > 0 ? popular : pairs.filter(p => POPULAR_PAIRS.includes(p.symbol)));
    } catch (err: any) {
      setError('Failed to fetch trading pairs');
      console.error('Error fetching trading pairs:', err);
      // Fallback to hardcoded popular pairs
      const fallbackPairs = POPULAR_PAIRS.map(symbol => ({
        symbol,
        baseAsset: symbol.split('/')[0],
        quoteAsset: symbol.split('/')[1],
        status: 'TRADING'
      }));
      setAllPairs(fallbackPairs);
      setPopularPairs(fallbackPairs);
    } finally {
      setLoading(false);
    }
  };

  const filteredPairs = allPairs.filter(pair =>
    pair.symbol.toLowerCase().includes(searchTerm.toLowerCase()) ||
    pair.baseAsset.toLowerCase().includes(searchTerm.toLowerCase()) ||
    pair.quoteAsset.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const togglePair = (symbol: string) => {
    const newSelection = selectedPairs.includes(symbol)
      ? selectedPairs.filter(p => p !== symbol)
      : [...selectedPairs, symbol];
    onSelectionChange(newSelection);
  };

  const removePair = (symbol: string) => {
    onSelectionChange(selectedPairs.filter(p => p !== symbol));
  };

  const addPopularPairs = () => {
    const combinedPairs = [...selectedPairs, ...POPULAR_PAIRS.slice(0, 5)];
    const uniquePairs = Array.from(new Set(combinedPairs));
    onSelectionChange(uniquePairs);
  };

  const clearAll = () => {
    onSelectionChange([]);
  };

  // Handle custom pair input
  const handleCustomPairChange = (value: string) => {
    setCustomPairInput(value);
    
    if (value.trim()) {
      const validation = SymbolValidator.validateSymbol(value);
      setCustomPairValidation(validation);
    } else {
      setCustomPairValidation({ isValid: true });
    }
  };

  const addCustomPair = () => {
    if (customPairValidation.isValid && customPairValidation.symbol) {
      const normalizedSymbol = customPairValidation.symbol;
      
      // Check if already selected
      if (!selectedPairs.includes(normalizedSymbol)) {
        onSelectionChange([...selectedPairs, normalizedSymbol]);
      }
      
      // Reset custom input
      setCustomPairInput('');
      setCustomPairValidation({ isValid: true });
      setShowAddCustom(false);
    }
  };

  const handleCustomPairKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addCustomPair();
    } else if (e.key === 'Escape') {
      setShowAddCustom(false);
      setCustomPairInput('');
      setCustomPairValidation({ isValid: true });
    }
  };

  const displayPairs = searchTerm ? filteredPairs : popularPairs;

  return (
    <div className={cn("relative", className)} ref={dropdownRef}>
      <div className="space-y-2">
        <label className="block text-sm font-medium text-foreground">
          Trading Pairs
        </label>
        
        {/* Selected pairs display */}
        <div className="min-h-[42px] w-full border border-input rounded-md p-2 bg-background focus-within:ring-2 focus-within:ring-primary focus-within:border-primary">
          <div className="flex flex-wrap gap-1">
            {selectedPairs.map(pair => (
              <span
                key={pair}
                className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-200 text-xs rounded-md"
              >
                {pair}
                <button
                  type="button"
                  onClick={() => removePair(pair)}
                  className="hover:bg-blue-200 dark:hover:bg-blue-800 rounded-full p-0.5"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
            <button
              type="button"
              onClick={() => setIsOpen(!isOpen)}
              className="inline-flex items-center gap-1 px-2 py-1 text-muted-foreground hover:text-foreground text-sm"
            >
              {selectedPairs.length === 0 ? 'Select trading pairs' : 'Add more'}
              <ChevronDown className={cn("w-4 h-4 transition-transform", isOpen && "rotate-180")} />
            </button>
          </div>
        </div>

        {/* Quick actions */}
        <div className="flex gap-2 text-xs">
          <button
            type="button"
            onClick={addPopularPairs}
            className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
          >
            Add Popular Pairs
          </button>
          <button
            type="button"
            onClick={() => setShowAddCustom(true)}
            className="text-green-600 dark:text-green-400 hover:text-green-800 dark:hover:text-green-300"
          >
            Add Custom Pair
          </button>
          {selectedPairs.length > 0 && (
            <button
              type="button"
              onClick={clearAll}
              className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300"
            >
              Clear All
            </button>
          )}
        </div>
      </div>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-background border border-input rounded-md shadow-lg max-h-80 overflow-hidden">
          {/* Search header */}
          <div className="p-3 border-b border-border">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Search trading pairs..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-input bg-background text-foreground rounded-md text-sm focus:ring-2 focus:ring-primary focus:border-primary"
              />
            </div>
          </div>

          {/* Custom pair input */}
          {showAddCustom && (
            <div className="p-3 border-b border-border bg-muted/50">
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Plus className="w-4 h-4 text-muted-foreground" />
                  <span className="text-sm font-medium text-foreground">Add Custom Trading Pair</span>
                </div>
                
                <div className="relative">
                  <input
                    type="text"
                    value={customPairInput}
                    onChange={(e) => handleCustomPairChange(e.target.value)}
                    onKeyPress={handleCustomPairKeyPress}
                    placeholder="e.g., DOGE/USDT or DOGEUSDT"
                    className={cn(
                      "w-full px-3 py-2 border rounded-md bg-background text-foreground text-sm",
                      "focus:ring-2 focus:ring-primary focus:border-primary",
                      customPairValidation.isValid 
                        ? "border-input" 
                        : "border-red-500 dark:border-red-400"
                    )}
                    autoFocus
                  />
                </div>

                {/* Validation feedback */}
                {!customPairValidation.isValid && customPairValidation.error && (
                  <div className="flex items-start gap-2 p-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
                    <AlertTriangle className="w-3 h-3 text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0" />
                    <div className="flex-1 text-xs">
                      <div className="text-red-800 dark:text-red-200 font-medium">
                        {customPairValidation.error}
                      </div>
                      {customPairValidation.suggestion && (
                        <div className="text-red-600 dark:text-red-300 mt-1">
                          {customPairValidation.suggestion}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Success feedback */}
                {customPairValidation.isValid && customPairValidation.symbol && customPairInput.trim() && (
                  <div className="text-xs text-green-600 dark:text-green-400">
                    ✓ Valid format: {customPairValidation.symbol}
                  </div>
                )}

                {/* Action buttons */}
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={addCustomPair}
                    disabled={!customPairValidation.isValid || !customPairInput.trim()}
                    className={cn(
                      "px-3 py-1 text-xs rounded-md font-medium",
                      "disabled:opacity-50 disabled:cursor-not-allowed",
                      customPairValidation.isValid && customPairInput.trim()
                        ? "bg-green-600 text-white hover:bg-green-700"
                        : "bg-muted text-muted-foreground"
                    )}
                  >
                    Add Pair
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddCustom(false);
                      setCustomPairInput('');
                      setCustomPairValidation({ isValid: true });
                    }}
                    className="px-3 py-1 text-xs rounded-md font-medium bg-muted text-muted-foreground hover:bg-muted/80"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Content */}
          <div className="max-h-64 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center p-4">
                <Loader2 className="w-5 h-5 animate-spin" />
                <span className="ml-2 text-sm text-muted-foreground">Loading trading pairs...</span>
              </div>
            ) : error ? (
              <div className="p-4 text-center text-red-600 dark:text-red-400 text-sm">
                {error}
                <button
                  onClick={fetchTradingPairs}
                  className="block mt-2 text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
                >
                  Retry
                </button>
              </div>
            ) : displayPairs.length === 0 ? (
              <div className="p-4 text-center text-muted-foreground text-sm">
                {searchTerm ? 'No trading pairs found' : 'No trading pairs available'}
              </div>
            ) : (
              <>
                {!searchTerm && popularPairs.length > 0 && (
                  <div className="p-2 border-b border-border">
                    <div className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-muted-foreground">
                      <Star className="w-3 h-3" />
                      Popular Pairs
                    </div>
                  </div>
                )}
                
                {displayPairs.map(pair => {
                  const isSelected = selectedPairs.includes(pair.symbol);
                  return (
                    <button
                      key={pair.symbol}
                      type="button"
                      onClick={() => togglePair(pair.symbol)}
                      className={cn(
                        "w-full px-3 py-2 text-left hover:bg-muted flex items-center justify-between",
                        isSelected && "bg-blue-50 dark:bg-blue-900/50"
                      )}
                    >
                      <div className="flex items-center gap-3">
                        <div className={cn(
                          "w-4 h-4 border border-input rounded flex items-center justify-center",
                          isSelected && "bg-blue-600 dark:bg-blue-500 border-blue-600 dark:border-blue-500"
                        )}>
                          {isSelected && <Check className="w-3 h-3 text-white" />}
                        </div>
                        <div>
                          <div className="font-medium text-sm text-foreground">{pair.symbol}</div>
                          <div className="text-xs text-muted-foreground">
                            {pair.baseAsset} / {pair.quoteAsset}
                          </div>
                        </div>
                      </div>
                      {pair.price && (
                        <div className="text-xs text-muted-foreground">
                          ${parseFloat(pair.price).toFixed(4)}
                        </div>
                      )}
                    </button>
                  );
                })}
                
                {searchTerm && filteredPairs.length > 0 && (
                  <div className="p-2 border-t border-border bg-muted">
                    <div className="text-xs text-muted-foreground text-center">
                      {filteredPairs.length} pair{filteredPairs.length !== 1 ? 's' : ''} found
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TradingPairSelector;